import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Text, Input, FilterOptions } from "react_reusable_components";
import {
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import Button from "../../components/Button";
import useResponsive from "../../core/hooks/useResponsive";
import ListingHeaderLayout from "../../layouts/ListingHeaderLayout";
import {
  getConditionalContent,
  getFilterData,
  getLocalizedMessage,
  getProductAndSubcategoryFromFilterData,
  getSelectedCountries,
  getSelectedRolesIds,
} from "../../UtilityFunctions/utils";
import Styles from "./UserListingTopContainer.module.scss";
import ThreeColumn from "../../core/layouts/ThreeColumn";
import variables from "../../themes/base/styles/variables";
import ProductCategoryService from "../../services/product-category-service";
import { ProductCategoryContext } from "../../globalContext/productCategory/productCategoryProvider";
import RoleServices from "../../services/roles-services";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { UserContext } from "../../globalContext/Users/userProvider";
import { setUserQuery } from "../../globalContext/Users/userActions";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { userRole as userRoles } from "../../constants";
const UserListingTopContainerStyle = {
  secondSectionMiddleSectionStyle: {
    display: variables.dFlex,
    gap: variables.gapSmaller,
    flexDirection: "row",
  },
  countStyle: {
    backgroundColor: "rgba(220, 0, 50, 0.12)",
    color: variables.primaryBg,
    height: variables.closeIcon,
    padding: variables.paddingXXSmall,
    display: variables.dFlex,
    justifyContent: variables.alignCenter,
    alignItems: variables.alignCenter,
    borderRadius: variables.countTagBorderRadius,
    fontSize: variables.fontSizeSmallest,
    fontWeight: variables.fontWeightBold,
    marginLeft: variables.marginSmall,
  },
};

const UserListingTopContainer = ({
  query,
  setQuery,
  setFilter,
  prevFilters,
  downloadUserReportsHandler,
}) => {
  const intl = useIntl();
  const responsive = useResponsive();
  const navigate = useNavigate();
  const loaderDispatch = useContext(GlobalLoaderContext)[1];

  const [userState, userDispatch] = useContext(UserContext);
  const [userRole, setUserRoles] = useState();
  const [open, setOpen] = useState(false);
  const [filtersCount, setFiltersCount] = useState(0);
  const [countriesList, setCountriesList] = useState();
  const [authState] = useContext(AuthContext);

  const handleInputChange = (e) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      searchStr: e.target.value,
    }));
  };

  const [productCategoryState, productCategoryDispatch] = useContext(
    ProductCategoryContext
  );

  useEffect(() => {
    ProductCategoryService.getProductCategoryList(
      productCategoryDispatch,
      {},
      intl,
      loaderDispatch
    );
    RoleServices.getRoles({ intl })
      .then((roles) => setUserRoles(roles))
      .catch(() => {}); //NOSONAR
  }, []);

  useEffect(() => {
    if (authState?.currentRole?.id === userRoles.ADMIN)
      ProductCategoryService.getCountries(intl).then((data) => {
        setCountriesList(data?.result);
      });
  }, [authState?.currentRole?.id]);

  const filterOptions = getFilterData(
    productCategoryState?.productCategoryList?.result,
    userRole,
    countriesList
  );

  const onApplyFilter = (data) => {
    const [productCategory, subCategory] =
      getProductAndSubcategoryFromFilterData(data?.[0]);

    const selectedRolesIds = getSelectedRolesIds(data?.[1]);
    const selectedCountries = getSelectedCountries(
      data?.find((item) => item?.value === "Country")
    );

    setFilter(() => ({
      productCategories: productCategory,
      subCategories: subCategory,
      roles: selectedRolesIds,
      countries: selectedCountries,
    }));
    setOpen(false);
    userDispatch(setUserQuery({ ...userState?.query, prevFilters: data }));
  };

  const onResetFilter = () => {
    setFiltersCount(0);
    setFilter({
      productCategories: [],
      subCategories: [],
      roles: [],
      countries: [],
    });
    userDispatch(setUserQuery({ ...userState?.query, prevFilters: [] }));
    setOpen(false);
  };

  useEffect(() => {
    let count = getCountValue(userState);

    setFiltersCount(count);
  }, [userState?.query?.prevFilters]);

  return (
    <ListingHeaderLayout
      firstSection={
        <Text className={Styles["heading-text"]}>
          {getLocalizedMessage(intl, "userManagement.listHeading")}
        </Text>
      }
      secondSection={
        <ThreeColumn
          className={Styles["second-section-style"]}
          leftSectionStyle={{ flex: 1 }}
          leftSection={
            <Input
              className={`${Styles["input-style"]} ${
                Styles[!responsive?.isMd && "input-style-mobile"]
              }`}
              placeholder={getLocalizedMessage(
                intl,
                "userManagement.searchPlaceholder"
              )}
              prefix={
                <SearchOutlined className={Styles["search-icon-style"]} />
              }
              onChange={handleInputChange}
              value={query.searchStr}
            />
          }
          middleSectionStyle={
            UserListingTopContainerStyle.secondSectionMiddleSectionStyle
          }
          middleSection={
            <>
              <FilterOptions
                defaultSelectedValue={userState?.query?.prevFilters}
                filterData={filterOptions}
                open={open}
                isMobile={!responsive.isMd}
                setOpen={setOpen}
                onReset={onResetFilter}
                onApply={onApplyFilter}
                mainContainerWidth={700}
                placement={getConditionalContent({
                  condition: responsive?.isMd,
                  trueValue: "bottomRight",
                  falseValue: "bottom",
                })}
                button={
                  <Button
                    icon={
                      <img
                        src="/icons/filter_list.svg"
                        className={Styles["filter-icon-style"]}
                      />
                    }
                    className={Styles["add-Filter"]}
                    type="text"
                    onClick={() => setOpen(true)}
                  >
                    {getConditionalContent({
                      condition: responsive.isSm,
                      trueValue: "Filters",
                    })}
                    {filtersCount > 0 && (
                      <span style={UserListingTopContainerStyle.countStyle}>
                        {`${filtersCount}`}
                      </span>
                    )}
                  </Button>
                }
              />
              <Button
                icon={
                  <DownloadOutlined className={Styles["plus-icon-style"]} />
                }
                className={Styles["add-Filter"]}
                type="text"
                onClick={downloadUserReportsHandler}
              >
                {getConditionalContent({
                  condition: responsive.isSm,
                  trueValue: getLocalizedMessage(intl, "button.generateReport"),
                })}
              </Button>
            </>
          }
          rightSection={
            <Button
              icon={<PlusOutlined className={Styles["plus-icon-style"]} />}
              className={Styles["add-button"]}
              onClick={() => navigate("/add-user")}
            >
              {responsive.isSm &&
                getLocalizedMessage(intl, "userManagement.addNewUser")}
            </Button>
          }
        />
      }
    />
  );
};

export default UserListingTopContainer;

function getCountValue(userState) {
  let count = 0;
  if (userState?.query?.prevFilters?.length) {
    for (const obj of userState?.query?.prevFilters) {
      for (const child of obj?.children) {
        if (child.isSelected) {
          count += 1;
          break; // Move to the next object since we found a selected child
        }
      }
    }
  }
  return count;
}
